<template>
  <v-container class="pa-0">
    <v-row dense row wrap justify="center" class="mb-1 mt-2">
      <v-col dense class="col-12">
        <div class="text-center">
          <v-btn-toggle
            mandatory
            v-model="localMapType"
            @change="handleChangeMapType"
            dense
          >
            <v-btn x-small color="primary" outlined value="marker"
              >Markers</v-btn
            >
            <v-btn x-small color="primary" outlined value="heatmap"
              >Heatmap</v-btn
            >
          </v-btn-toggle>
        </div>
      </v-col>
    </v-row>
    <v-row dense row wrap justify="center">
      <v-col class="col-12">
        <!-- TODO clean up unused props -->
        <EventsPickerV3
          :topic-filter-counts="topicFilterCounts"
          :reset="reset"
          :selected-topics="selectedTopics"
          @change="handleChangeEvents"
          @change-has-topics-selected="handleChangeHasTopicsSelected"
        ></EventsPickerV3>

        <div class="clearfix"></div>
      </v-col>
    </v-row>
    <v-row v-if="isSentimentFilterUnlocked">
      <v-col class="col-12" style="font-size: 12px;">
        <div>
          Sentiment:
          <v-icon
            small
            color="blue"
            v-on:click.stop.prevent="showSentimentPopup = !showSentimentPopup"
            title="Sentiment description"
            class="description-icon"
            >fas fa-info-circle</v-icon
          >
          <SentimentInfoDialog :open="showSentimentPopup"></SentimentInfoDialog>
        </div>
        <SearchFilterSentiment
          @change="handleChangeSentiment"
          :filter-counts="sentimentFilterCounts"
          :incoming-selection="selectedSentiment"
        ></SearchFilterSentiment>
      </v-col>
    </v-row>
    <v-row v-if="isEmotionFilterUnlocked">
      <v-col class="col-12" style="font-size: 12px;">
        <div>
          Emotions:
          <v-icon
            small
            color="blue"
            v-on:click.stop.prevent="showEmotionPopup = !showEmotionPopup"
            title="Emotion description"
            class="description-icon"
            >fas fa-info-circle</v-icon
          >
          <EmotionInfoDialog :open="showEmotionPopup"></EmotionInfoDialog>
        </div>
        <SearchFilterEmotions
          @change="handleChangeEmotions"
          :filter-counts="emotionsFilterCounts"
          :incoming-selection="selectedEmotions"
        ></SearchFilterEmotions>
      </v-col>
    </v-row>
    <v-row
      v-if="!hotspotsOnlySelected && (type === 'detected' || type === 'all')"
      dense
      row
      wrap
    >
      <v-col class="col-12 pull-left" style="font-size: 12px;">
        <div>Sources:</div>
        <SearchFilterSource
          :reset="reset"
          :selected="selectedSources"
          :source-filter-counts="sourceFilterCounts"
          @change="handleChangeSource"
          style="margin-left: -6px;"
        ></SearchFilterSource>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <SearchFilterGeolocationPrecision
          v-if="isLiveSearchGeolocationPrecisionUnlocked"
          :type="type"
          :geolocation-precision-filter-counts="
            geolocationPrecisionFilterCounts
          "
          :given="selectedGeolocationPrecisions"
          :reset="reset"
          @change="handleChangeGeolocationPrecision"
        ></SearchFilterGeolocationPrecision>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <SearchSeveritySlider
          v-if="isLiveSearchEventsSeverityUnlocked"
          :selected="selectedSeverityRange"
          @change="handleChangeSeverity"
        ></SearchSeveritySlider>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import lodashCloneDeep from "lodash/cloneDeep";
import lodashOrderBy from "lodash/orderBy";
import lodashGet from "lodash/get";

import { getPrimaryTopics } from "@/services/events/topics.service";

const EMITTED_ACTIONS = {
  CHANGE: "change",
  CHANGE_MAP_TYPE: "change-map-type",
  CHANGE_SEVERITY: "change-severity",
  CHANGE_GEOLOCATION_PRECISIONS: "change-geolocation-precisions",
  CHANGE_SENTIMENT: "change-sentiment",
  CHANGE_EMOTIONS: "change-emotions",
};

const EVENT_PICKER_ACTIONS = {
  CHANGED_LIVE_SEARCH: "changed-live-search",
  CHANGE_HAS_TOPICS_SELECTED: "change-has-topics-selected",
};

export default {
  name: "SearchFilterEventsDetected",
  components: {
    SearchFilterSource: () => import("@/components/search/SearchFilterSource"),
    EventsPickerV3: () => import("@/components/events/EventsPickerV3"),
    SearchSeveritySlider: () =>
      import("@/components/search/SearchSeveritySlider.vue"),
    SearchFilterGeolocationPrecision: () =>
      import("@/components/search/SearchFilterGeolocationPrecision"),
    SearchFilterSentiment: () =>
      import("@/components/search/SearchFilterSentiment"),
    SearchFilterEmotions: () =>
      import("@/components/search/SearchFilterEmotions"),
    SentimentInfoDialog: () =>
      import("@/components/common/dialogs/SentimentInfoDialog"),
    EmotionInfoDialog: () =>
      import("@/components/common/dialogs/EmotionInfoDialog"),
  },
  props: {
    selectedTopics: {
      type: String,
    },
    type: {
      type: String,
      validator: (value) => {
        return ["all", "detected"].indexOf(value) !== -1;
      },
    },
    reset: {
      type: String,
    },
    topicFilterCounts: {
      type: Object,
    },
    hotspotsOnlySelected: {
      type: Boolean,
    },
    sourceFilterCounts: {
      type: Object,
    },
    sentimentFilterCounts: {
      type: Object,
    },
    emotionsFilterCounts: {
      type: Object,
    },
    selectedSentiment: {
      type: Array,
    },
    selectedEmotions: {
      type: Array,
    },
    selectedSources: {
      type: Object,
    },
    mapType: {
      type: String,
      default: "marker",
    },
    selectedSeverity: {
      type: Object,
    },
    selectedGeolocationPrecisions: {
      type: Array,
    },
    geolocationPrecisionFilterCounts: {
      type: Object,
    },
  },
  data() {
    return {
      primaryTopics: null,
      selectedPrimaryTopicType: null,
      sources: null,
      localMapType: null,
      showSentimentPopup: false,
      showEmotionPopup: false,
    };
  },
  mounted() {
    this.init();
  },
  computed: {
    selectedSeverityRange() {
      if (this.selectedSeverity) {
        return [
          this.selectedSeverity.severityMin || 0,
          this.selectedSeverity.severityMax || 10,
        ];
      }
      return null;
    },
    isLiveSearchEventsSeverityUnlocked() {
      return this.featureUnlocked("livesearch-events-severity");
    },
    isLiveSearchGeolocationPrecisionUnlocked() {
      return this.featureUnlocked("livesearch-geolocation-precision");
    },
    isSentimentFilterUnlocked() {
      return this.featureUnlocked("livesearch-sentiment-filter");
    },
    isEmotionFilterUnlocked() {
      return this.featureUnlocked("livesearch-emotion-filter");
    },
  },
  methods: {
    init() {
      let topics = [];
      const PRIMARY_TOPICS = lodashCloneDeep(getPrimaryTopics());
      const SORTED_TOPICS = lodashOrderBy(PRIMARY_TOPICS, ["order"], ["asc"]);
      SORTED_TOPICS.forEach((topic) => {
        if (
          (topic.type === "threat" || topic.type === "insult") &&
          !this.featureUnlocked("livesearch-threat-insult-filter")
        ) {
          return;
        }
        if (this.featureUnlocked(`topic-${topic.type}`)) {
          let subtopics = [];
          if (topic.subtopics.length) {
            topic.subtopics.forEach((sTopic) => {
              if (this.featureUnlocked(`topic-${sTopic.apiName}`)) {
                subtopics.push(sTopic);
              }
            });
            topic.subtopics = subtopics;
          }
          topics.push(topic);
        }
      });
      this.primaryTopics = topics;
    },
    handleClickEventsBtn(topic, selected) {
      this.$emit(EMITTED_ACTIONS.CHANGE, { topic, selected });
    },
    handleClickType() {
      this.selectedPrimaryTopicType = null;
    },
    handleClickPrimaryTopic(topic) {
      if (topic && topic.type) {
        this.selectedPrimaryTopicType = topic.type;
      }
    },
    handleChangeSource(sources) {
      this.$emit(EMITTED_ACTIONS.CHANGE, {
        sources: sources,
      });
    },
    handleChangeGeolocationPrecision(geolocationPrecisions) {
      this.$emit(
        EMITTED_ACTIONS.CHANGE_GEOLOCATION_PRECISIONS,
        geolocationPrecisions
      );
    },
    handleChangeEvents(event) {
      this.$emit(EMITTED_ACTIONS.CHANGE, event);
    },
    handleChangeHasTopicsSelected(val) {
      this.$emit(EVENT_PICKER_ACTIONS.CHANGE_HAS_TOPICS_SELECTED, val);
    },
    handleChangeMapType(val) {
      this.$emit(EMITTED_ACTIONS.CHANGE_MAP_TYPE, val);
    },
    handleChangeSeverity(val) {
      this.$emit(EMITTED_ACTIONS.CHANGE_SEVERITY, {
        severityMin: lodashGet(val, "[0]") || 0,
        severityMax: lodashGet(val, "[1]") || 0,
      });
    },
    handleChangeSentiment(val) {
      this.$emit(EMITTED_ACTIONS.CHANGE_SENTIMENT, val);
    },
    handleChangeEmotions(val) {
      this.$emit(EMITTED_ACTIONS.CHANGE_EMOTIONS, val);
    },
  },
  watch: {
    mapType: {
      handler() {
        this.localMapType = this.mapType;
      },
      immediate: true,
    },
  },
};
</script>

<style>
.description-icon {
  margin-left: 5px;
  top: -2px;
}
</style>
